// client/src/components/DeviceForm.jsx
// Create/edit form for a Device. A device is either a handheld TERMINAL
// (teacher / sales, with an EARN/SPEND/BOTH mode) or a fixed self-service
// KIOSK (10" station) whose extra settings are an availability schedule.
//
// Same dirty-tracking + scroll-to-error + actionable Hebrew error pattern as
// the other forms (CampForm, StudentForm).

import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../lib/api.js';
import { ExclamationTriangleIcon } from './Icons.jsx';
import TimeSelect from './TimeSelect.jsx';
import ScreensaverField from './ScreensaverField.jsx';

const DEFAULT_WINDOW = { start: '08:00', end: '14:00' };

// Modes whose windows editor is relevant (ALWAYS and BLOCKED_ALWAYS ignore
// the window list entirely).
const WINDOWED_MODES = ['ACTIVE_WINDOWS', 'BLOCKED_WINDOWS'];

// Keep availability windows in chronological order (by start, then end). Times
// are zero-padded "HH:MM" so a plain string compare sorts correctly.
function sortWindows(list) {
  return [...list].sort(
    (a, b) =>
      String(a.start || '').localeCompare(String(b.start || '')) ||
      String(a.end || '').localeCompare(String(b.end || ''))
  );
}

export default function DeviceForm({
  initial,
  campId,
  onSubmit,
  onCancel,
  submitLabel,
  onDirtyChange,
}) {
  const [name, setName] = useState(initial?.name || '');
  const [kind, setKind] = useState(initial?.kind || 'TERMINAL');
  const [mode, setMode] = useState(initial?.mode || 'EARN');
  const [availMode, setAvailMode] = useState(initial?.availabilityMode || 'ALWAYS');
  const [windows, setWindows] = useState(
    Array.isArray(initial?.availabilityWindows) ? initial.availabilityWindows : []
  );
  // Kiosk admin code — 6 digits typed "like a card" to open the on-device admin
  // menu (connectivity test, etc.). Empty = no admin access.
  const [adminCode, setAdminCode] = useState(initial?.kioskAdminCode || '');
  // Kiosk manual entry — allow typing the last 8 card digits (default) or
  // require a physical swipe only.
  const [manualEntry, setManualEntry] = useState(initial?.kioskManualEntry !== false);
  // What a blocked kiosk shows (ADR-019): hours screen / black / screensaver.
  const [blockedDisplay, setBlockedDisplay] = useState(initial?.kioskBlockedDisplay || 'HOURS');
  const [errors, setErrors] = useState({});
  const [topError, setTopError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const topErrorRef = useRef(null);

  const initialFlat = useMemo(
    () =>
      JSON.stringify({
        name: initial?.name || '',
        kind: initial?.kind || 'TERMINAL',
        mode: initial?.mode || 'EARN',
        availMode: initial?.availabilityMode || 'ALWAYS',
        windows: Array.isArray(initial?.availabilityWindows) ? initial.availabilityWindows : [],
        adminCode: initial?.kioskAdminCode || '',
        manualEntry: initial?.kioskManualEntry !== false,
        blockedDisplay: initial?.kioskBlockedDisplay || 'HOURS',
      }),
    [initial]
  );
  const currentFlat = JSON.stringify({ name, kind, mode, availMode, windows, adminCode, manualEntry, blockedDisplay });
  const isDirty = currentFlat !== initialFlat;

  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (topError && topErrorRef.current) {
      topErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [topError]);

  // Switching to a windowed mode with no windows yet → seed one so the user
  // sees the editor instead of an empty void. (ALWAYS and BLOCKED_ALWAYS
  // don't use windows.)
  function pickAvailMode(next) {
    setAvailMode(next);
    if (WINDOWED_MODES.includes(next) && windows.length === 0) setWindows([{ ...DEFAULT_WINDOW }]);
  }
  // 6-digit random admin code (crypto when available). Leading zeros allowed —
  // it's typed verbatim on the kiosk.
  function genAdminCode() {
    let n;
    if (window.crypto && window.crypto.getRandomValues) {
      const a = new Uint32Array(1);
      window.crypto.getRandomValues(a);
      n = a[0] % 1000000;
    } else {
      n = Math.floor(Math.random() * 1000000);
    }
    setAdminCode(String(n).padStart(6, '0'));
  }
  // No live re-sorting while editing — reordering rows mid-edit makes the row
  // the user is changing jump around. Windows are sorted once on save (and on
  // load) so the stored/displayed order is chronological without disrupting input.
  function addWindow() { setWindows((w) => [...w, { ...DEFAULT_WINDOW }]); }
  function removeWindow(i) { setWindows((w) => w.filter((_, idx) => idx !== i)); }
  function setWindowField(i, field, value) {
    setWindows((w) => w.map((win, idx) => (idx === i ? { ...win, [field]: value } : win)));
  }

  function handleCancel() {
    if (isDirty && !window.confirm('יש שינויים שלא נשמרו.\nלבטל?')) return;
    onCancel();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErrors({});
    setTopError(null);

    // Client-side guards for the kiosk schedule.
    // Validate any windows that exist (we persist them even under ALWAYS, so
    // they survive toggling back to a windowed mode).
    if (kind === 'KIOSK' && windows.length > 0 &&
        windows.some((w) => !w.start || !w.end || w.start >= w.end)) {
      setTopError('בכל טווח שעת ההתחלה חייבת להיות לפני שעת הסיום.');
      return;
    }
    // A windowed mode needs at least one window (ALWAYS / BLOCKED_ALWAYS don't).
    if (kind === 'KIOSK' && WINDOWED_MODES.includes(availMode) && windows.length === 0) {
      setTopError('הוסיפו לפחות טווח שעות אחד, או בחרו "זמין כל היום".');
      return;
    }

    // Admin code (kiosk): empty = no admin access; otherwise exactly 6 digits.
    if (kind === 'KIOSK' && adminCode && !/^\d{6}$/.test(adminCode)) {
      setTopError('קוד המנהל חייב להיות בדיוק 6 ספרות, או ריק לביטול הגישה.');
      return;
    }

    const body =
      kind === 'KIOSK'
        ? {
            name: name.trim(),
            kind: 'KIOSK',
            availabilityMode: availMode,
            // Persist the windows the user configured even under ALWAYS — they're
            // simply ignored while ALWAYS, but kept so switching back to a
            // windowed mode restores them instead of starting from scratch.
            // Sorted chronologically (state is already sorted; belt-and-braces).
            availabilityWindows: windows.length ? sortWindows(windows) : null,
            kioskAdminCode: adminCode || null,
            kioskManualEntry: manualEntry,
            kioskBlockedDisplay: blockedDisplay,
          }
        : { name: name.trim(), kind: 'TERMINAL', mode };

    setSubmitting(true);
    try {
      await onSubmit(body);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 422 && Array.isArray(err.details)) {
          const fieldErrors = {};
          for (const issue of err.details) fieldErrors[issue.path] = issue.message;
          setErrors(fieldErrors);
          setTopError('יש לתקן את השדות המסומנים ולנסות שוב.');
        } else if (err.code === 'service_unavailable' && err.message === 'code_pool_exhausted') {
          setTopError('לא ניתן להקצות קוד פנוי כעת. נסה שוב, ואם הבעיה ממשיכה — מחק מכשירים שאינם בשימוש.');
        } else if (err.status === 422) {
          setTopError(
            err.message && err.message !== err.code
              ? `שגיאת ולידציה: ${err.message}`
              : 'אחד השדות לא תקין. בדוק את הערכים ונסה שוב.'
          );
        } else if (err.status === 401) {
          setTopError('הסשן פג. נסה לרענן את הדף ולהיכנס מחדש.');
        } else if (err.status === 403) {
          setTopError('אין לך הרשאה לפעולה זו.');
        } else if (err.status >= 500) {
          setTopError(`שגיאת שרת (${err.status}). נסה שוב בעוד רגע.`);
        } else {
          setTopError(`שגיאה: ${err.message || err.code || 'לא צפויה'}`);
        }
      } else {
        setTopError('שגיאה לא צפויה. בדוק שיש חיבור אינטרנט ונסה שוב.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {topError && (
        <div
          ref={topErrorRef}
          role="alert"
          className="rounded-2xl bg-joy-50 border border-joy-200 text-joy-900 px-4 py-3 text-sm font-medium flex items-start gap-2 animate-slide-up"
        >
          <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5" />
          <span>{topError}</span>
        </div>
      )}

      <label className="block">
        <span className="label">
          שם המכשיר <span className="text-joy-600">*</span>
        </span>
        <input
          type="text"
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={50}
          autoFocus
        />
        {errors.name && <span className="field-error">{errors.name}</span>}
      </label>

      {/* Device kind: handheld terminal vs fixed self-service kiosk. */}
      <fieldset>
        <legend className="label mb-2">סוג המכשיר</legend>
        <div className="grid grid-cols-2 gap-3">
          <KindOption
            value="TERMINAL"
            label="מסופון"
            description="מכשיר נייד למורה / מכירה"
            selected={kind === 'TERMINAL'}
            onChange={setKind}
          />
          <KindOption
            value="KIOSK"
            label="עמדת שירות עצמי"
            description="עמדת קיוסק קבועה לתלמידים"
            selected={kind === 'KIOSK'}
            onChange={setKind}
          />
        </div>
      </fieldset>

      {kind === 'TERMINAL' ? (
        <fieldset>
          <legend className="label mb-2">
            מצב המסופון <span className="text-joy-600">*</span>
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <ModeOption value="EARN" label="מורה · זיכוי" description="רק הוספת נקודות" tone="mint" selected={mode === 'EARN'} onChange={setMode} />
            <ModeOption value="SPEND" label="חנות · חיוב" description="רק עמדת מכירה" tone="sky" selected={mode === 'SPEND'} onChange={setMode} />
            <ModeOption value="BOTH" label="מורה + חנות" description="בחירה בכל כניסה" tone="dual" selected={mode === 'BOTH'} onChange={setMode} />
          </div>
          {errors.mode && <span className="field-error">{errors.mode}</span>}
        </fieldset>
      ) : (
        <fieldset className="rounded-2xl border border-ink-200 bg-ink-50/40 p-4 space-y-3">
          <legend className="label px-1">זמינות העמדה</legend>
          <p className="hint -mt-1">בתלמיד סורק כרטיס לבירור יתרה והיסטוריה, וטוען שוברי נקודות בעצמו.</p>

          <div className="space-y-2">
            <AvailOption value="ALWAYS" label="זמינה כל היום" selected={availMode === 'ALWAYS'} onChange={pickAvailMode} />
            <AvailOption value="ACTIVE_WINDOWS" label="פעילה רק בטווחי שעות מסוימים" selected={availMode === 'ACTIVE_WINDOWS'} onChange={pickAvailMode} />
            <AvailOption value="BLOCKED_WINDOWS" label="פעילה תמיד, חסומה בטווחי שעות מסוימים" selected={availMode === 'BLOCKED_WINDOWS'} onChange={pickAvailMode} />
            <AvailOption value="BLOCKED_ALWAYS" label="חסומה כל היום" selected={availMode === 'BLOCKED_ALWAYS'} onChange={pickAvailMode} />
          </div>

          {!WINDOWED_MODES.includes(availMode) && windows.length > 0 && (
            <p className="hint pt-1">טווחי השעות שהגדרת נשמרים, ויחזרו אם תבחר שוב מצב מוגבל.</p>
          )}

          {WINDOWED_MODES.includes(availMode) && (
            <div className="space-y-2 pt-1">
              <div className="text-xs font-bold text-ink-600">
                {availMode === 'ACTIVE_WINDOWS' ? 'טווחי השעות שבהם העמדה פעילה:' : 'טווחי השעות שבהם העמדה חסומה:'}
              </div>
              {windows.map((w, i) => (
                <div key={i} className="flex items-center gap-2">
                  <TimeSelect value={w.start} onChange={(v) => setWindowField(i, 'start', v)} />
                  <span className="text-ink-400 font-bold">–</span>
                  <TimeSelect value={w.end} onChange={(v) => setWindowField(i, 'end', v)} />
                  <button
                    type="button"
                    onClick={() => removeWindow(i)}
                    className="shrink-0 w-9 h-9 grid place-items-center rounded-xl text-joy-700 hover:bg-joy-50"
                    aria-label="הסר טווח"
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button type="button" onClick={addWindow} className="btn-secondary btn-sm">+ טווח שעות</button>
              <p className="hint">לפי שעון ישראל.</p>
            </div>
          )}
        </fieldset>
      )}

      {kind === 'KIOSK' && availMode !== 'ALWAYS' && (
        <fieldset className="rounded-2xl border border-ink-200 bg-ink-50/40 p-4 space-y-2">
          <legend className="label px-1">מה מוצג בזמן חסימה</legend>
          <p className="hint -mt-1">
            התצוגה בעמדה בשעות שבהן היא חסומה. חלה על כל זמן חסימה — לפי טווחי
            השעות או כשחסומה כל היום.
          </p>
          <div className="space-y-2">
            <BlockedDisplayOption
              value="HOURS"
              label='מסך "העמדה סגורה כעת" עם שעות הפעילות'
              selected={blockedDisplay === 'HOURS'}
              onChange={setBlockedDisplay}
            />
            <BlockedDisplayOption
              value="BLACK"
              label="מסך שחור ריק — ללא כל הודעה"
              selected={blockedDisplay === 'BLACK'}
              onChange={setBlockedDisplay}
            />
            <BlockedDisplayOption
              value="IMAGE"
              label="תמונת שומר מסך במסך מלא"
              selected={blockedDisplay === 'IMAGE'}
              onChange={setBlockedDisplay}
            />
          </div>
          {blockedDisplay === 'IMAGE' && (
            initial?.id ? (
              <ScreensaverField campId={campId} device={initial} />
            ) : (
              <p className="hint pt-1">שמרו את המכשיר תחילה, ואז תוכלו להעלות שומר מסך.</p>
            )
          )}
        </fieldset>
      )}

      {kind === 'KIOSK' && (
        <fieldset className="rounded-2xl border border-ink-200 bg-ink-50/40 p-4 space-y-2">
          <legend className="label px-1">הקלדה ידנית של מספר כרטיס</legend>
          <p className="hint -mt-1">
            כשההקלדה הידנית חסומה, כפתור ההקלדה מוסתר בעמדה ובירור יתרה אפשרי
            רק בהעברת הכרטיס פיזית בקורא.
          </p>
          <div className="space-y-2">
            <ManualEntryOption
              value={true}
              label="מותרת — אפשר להקליד את 8 הספרות האחרונות"
              selected={manualEntry}
              onChange={setManualEntry}
            />
            <ManualEntryOption
              value={false}
              label="חסומה — רק העברת כרטיס בקורא"
              selected={!manualEntry}
              onChange={setManualEntry}
            />
          </div>
        </fieldset>
      )}

      {kind === 'KIOSK' && (
        <fieldset className="rounded-2xl border border-ink-200 bg-ink-50/40 p-4 space-y-2">
          <legend className="label px-1">קוד מנהל לעמדה</legend>
          <p className="hint -mt-1">
            קוד בן 6 ספרות שמקלידים בעמדה (כמו העברת כרטיס, בלי כפתור) כדי לפתוח
            את תפריט המנהל — בדיקת קליטה וכלי התקנה. השאירו ריק כדי שלא תהיה גישת מנהל.
          </p>
          <div className="flex items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              dir="ltr"
              className="input num tracking-[0.3em] text-center font-bold"
              value={adminCode}
              onChange={(e) => setAdminCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="——————"
              maxLength={6}
              aria-label="קוד מנהל"
            />
            <button type="button" className="btn-secondary shrink-0" onClick={genAdminCode}>
              אקראי
            </button>
            {adminCode && (
              <button
                type="button"
                className="btn-ghost btn-sm shrink-0"
                onClick={() => setAdminCode('')}
              >
                ניקוי
              </button>
            )}
          </div>
        </fieldset>
      )}

      {!initial && <p className="hint">קוד 6 ספרות יוקצה אוטומטית לאחר היצירה.</p>}

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" className="btn-secondary" onClick={handleCancel}>
          ביטול
        </button>
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? 'שומר...' : submitLabel || 'שמור'}
        </button>
      </div>
    </form>
  );
}

function KindOption({ value, label, description, selected, onChange }) {
  return (
    <label
      className={`block rounded-2xl border-2 p-3 cursor-pointer transition-all ${
        selected ? 'border-brand-500 bg-brand-50 shadow-card' : 'border-ink-200 hover:border-brand-300 hover:bg-brand-50/40'
      }`}
    >
      <input type="radio" name="kind" value={value} checked={selected} onChange={(e) => onChange(e.target.value)} className="sr-only" />
      <div className="font-bold text-sm text-ink-900">{label}</div>
      <div className="text-xs text-ink-500 mt-0.5">{description}</div>
    </label>
  );
}

function ManualEntryOption({ value, label, selected, onChange }) {
  return (
    <label
      className={`flex items-center gap-3 rounded-xl border-2 px-3 py-2.5 cursor-pointer transition-all ${
        selected ? 'border-brand-500 bg-white shadow-card' : 'border-ink-200 bg-white hover:border-brand-300'
      }`}
    >
      <input
        type="radio"
        name="manualEntry"
        checked={selected}
        onChange={() => onChange(value)}
        className="w-4 h-4 accent-brand-600"
      />
      <span className="text-sm font-semibold text-ink-800">{label}</span>
    </label>
  );
}

function BlockedDisplayOption({ value, label, selected, onChange }) {
  return (
    <label
      className={`flex items-center gap-3 rounded-xl border-2 px-3 py-2.5 cursor-pointer transition-all ${
        selected ? 'border-brand-500 bg-white shadow-card' : 'border-ink-200 bg-white hover:border-brand-300'
      }`}
    >
      <input
        type="radio"
        name="blockedDisplay"
        value={value}
        checked={selected}
        onChange={(e) => onChange(e.target.value)}
        className="w-4 h-4 accent-brand-600"
      />
      <span className="text-sm font-semibold text-ink-800">{label}</span>
    </label>
  );
}

function AvailOption({ value, label, selected, onChange }) {
  return (
    <label
      className={`flex items-center gap-3 rounded-xl border-2 px-3 py-2.5 cursor-pointer transition-all ${
        selected ? 'border-brand-500 bg-white shadow-card' : 'border-ink-200 bg-white hover:border-brand-300'
      }`}
    >
      <input type="radio" name="availMode" value={value} checked={selected} onChange={(e) => onChange(e.target.value)} className="w-4 h-4 accent-brand-600" />
      <span className="text-sm font-semibold text-ink-800">{label}</span>
    </label>
  );
}

const MODE_TONE = {
  mint: {
    selected: 'border-mint-500 bg-mint-50 shadow-card',
    idle:     'border-ink-200 hover:border-mint-300 hover:bg-mint-50/40',
    strap:    'bg-mint-gradient',
  },
  sky: {
    selected: 'border-sky-500 bg-sky-50 shadow-card',
    idle:     'border-ink-200 hover:border-sky-300 hover:bg-sky-50/40',
    strap:    'bg-sky-gradient',
  },
  dual: {
    selected: 'border-brand-500 bg-brand-50 shadow-card',
    idle:     'border-ink-200 hover:border-brand-300 hover:bg-brand-50/40',
    strap:    'bg-gradient-to-l from-mint-500 via-brand-500 to-sky-500',
  },
};

function ModeOption({ value, label, description, tone, selected, onChange }) {
  const cls = MODE_TONE[tone];
  return (
    <label
      className={`relative block rounded-2xl border-2 overflow-hidden cursor-pointer transition-all
        ${selected ? cls.selected : cls.idle}`}
    >
      <div className={`h-1 ${cls.strap}`} />
      <input type="radio" name="mode" value={value} checked={selected} onChange={(e) => onChange(e.target.value)} className="sr-only" />
      <div className="p-3">
        <div className="font-bold text-sm text-ink-900">{label}</div>
        <div className="text-xs text-ink-500 mt-0.5">{description}</div>
      </div>
    </label>
  );
}
